import React, { useEffect, useState } from 'react';
import {
  Camera,
  ChevronDown,
  ChevronUp,
  LockKeyhole,
} from 'lucide-react';

const inputClass = (error, locked = false) =>
  `h-12 w-full rounded-xl border px-4 text-sm outline-none transition ${
    error
      ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-100'
      : locked
        ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500'
        : 'border-gray-300 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
  }`;

const FieldError = ({ message }) =>
  message ? (
    <p className="mt-1 text-xs font-medium text-red-600">
      {message}
    </p>
  ) : null;

const Label = ({ children, required = false, optional = false }) => (
  <label className="mb-2 block text-sm font-medium text-gray-700">
    {children}
    {required && <span className="text-red-500"> *</span>}
    {optional && (
      <span className="font-normal text-gray-400"> (Optional)</span>
    )}
  </label>
);

const StaffBasicDetails = ({
  formData,
  setFormData,
  handleChange,
  errors = {},
  emailLocked = false,
}) => {
  const [photoPreview, setPhotoPreview] = useState('');
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (formData.photoUrl) {
      setPhotoPreview(formData.photoUrl);
    }
  }, [formData.photoUrl]);

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPhotoPreview(URL.createObjectURL(file));
    setFormData((current) => ({
      ...current,
      photo: file,
    }));
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex w-full items-center justify-between border-b border-gray-100 px-6 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
            1
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Basic Details
            </h2>
            <p className="text-xs text-gray-500">
              Personal information and primary contact details.
            </p>
          </div>
        </div>

        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div className="p-6">
          <div className="mb-7 flex items-center gap-5">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-gray-400">
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt="Staff preview"
                  className="h-full w-full object-cover"
                />
              ) : (
                <Camera className="h-7 w-7" />
              )}
            </div>

            <label className="cursor-pointer rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Upload Photo
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoChange}
                className="hidden"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <Label required>First Name</Label>
              <input
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter first name"
                className={inputClass(errors.firstName)}
              />
              <FieldError message={errors.firstName} />
            </div>

            <div>
              <Label optional>Middle Name</Label>
              <input
                name="middleName"
                value={formData.middleName}
                onChange={handleChange}
                placeholder="Enter middle name"
                className={inputClass(errors.middleName)}
              />
              <FieldError message={errors.middleName} />
            </div>

            <div>
              <Label optional>Last Name</Label>
              <input
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter last name"
                className={inputClass(errors.lastName)}
              />
              <FieldError message={errors.lastName} />
            </div>

            <div>
              <Label required>Gender</Label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className={inputClass(errors.gender)}
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              <FieldError message={errors.gender} />
            </div>

            <div>
              <Label required>Date of Birth</Label>
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                className={inputClass(errors.dob)}
              />
              <FieldError message={errors.dob} />
            </div>

            <div>
              <Label optional>Phone</Label>
              <input
                type="tel"
                inputMode="tel"
                maxLength={20}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                className={inputClass(errors.phone)}
              />
              <FieldError message={errors.phone} />
            </div>

            <div className="lg:col-span-2">
              <Label required={formData.enableLogin} optional={!formData.enableLogin}>
                Email
              </Label>
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={emailLocked}
                  placeholder="Enter email address"
                  className={`${inputClass(errors.email, emailLocked)} ${
                    emailLocked ? 'pr-10' : ''
                  }`}
                />
                {emailLocked && (
                  <LockKeyhole className="absolute right-3 top-3.5 h-4 w-4 text-gray-400" />
                )}
              </div>
              {emailLocked && (
                <p className="mt-1 text-xs text-gray-500">
                  This email is linked to the login account and is locked.
                </p>
              )}
              <FieldError message={errors.email} />
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <Label optional>Address</Label>
              <textarea
                rows={4}
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter address"
                className={`w-full resize-none rounded-xl border px-4 py-3 text-sm outline-none transition ${
                  errors.address
                    ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-100'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                }`}
              />
              <FieldError message={errors.address} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffBasicDetails;
